/**
 * CaseTags.jsx
 *
 * Renders a case's tag pills + a "+ Add tag" combobox.
 *   • User tags show a removable × (gated by canWrite).
 *   • System tags render locked (no ×, a small lock icon).
 *   • The combobox autocompletes from the existing vocabulary and offers
 *     "Create <text>" when no exact match exists. Fully keyboard-navigable
 *     (↑/↓ to move, Enter to choose, Esc to close).
 *
 * Tag labels are rendered as React text children → auto-escaped (no XSS).
 * All optimistic mutations re-fetch via onChange so the parent owns truth.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTags, addCaseTag, removeCaseTag } from '../api/client.js';
import { tagColourClass, tagPillStyle, tagSwatchStyle } from '../utils/tagColors.js';

const LockIcon = () => (
  <svg className="tag-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// canWrite  — may apply existing tags + remove user tags (Admin and above).
// canCreate — may create a NEW tag from the picker (Supervisor / SuperAdmin only);
//             a subset of canWrite. When false the "Create <text>" row is hidden,
//             so lower roles can still tag cases from the existing vocabulary.
export default function CaseTags({ caseId, tags = [], canWrite = false, canCreate = false, onChange }) {
  const [vocab, setVocab]     = useState([]);     // all available tags
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const [active, setActive]   = useState(0);      // highlighted menu index
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const boxRef   = useRef(null);
  const inputRef = useRef(null);

  // Load the vocabulary lazily the first time the combobox opens.
  useEffect(() => {
    if (!open || vocab.length) return;
    let alive = true;
    getTags().then(v => { if (alive) setVocab(v); }).catch(() => {});
    return () => { alive = false; };
  }, [open, vocab.length]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e) { if (boxRef.current && !boxRef.current.contains(e.target)) close(); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus the input when the combobox opens.
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const appliedIds = useMemo(() => new Set(tags.map(t => t.id)), [tags]);

  // Suggestions: vocabulary not already on the case, matched against the query.
  // System/rule tags are EXCLUDED — they're auto-applied by the rules engine and
  // cannot be added manually (the API also rejects adding them, defence in depth).
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vocab
      .filter(t => !t.isSystem)
      .filter(t => !appliedIds.has(t.id))
      // Empty query → only tags marked visible-in-dropdown; typing → search ALL
      // (so search-only tags are still findable). Undefined ⇒ treated as visible.
      .filter(t => q ? t.label.toLowerCase().includes(q) : t.visibleInDropdown !== false)
      .slice(0, 8);
  }, [vocab, appliedIds, query]);

  // Offer "Create" when the trimmed query has no exact (case-insensitive) match
  // anywhere in the vocabulary.
  const trimmed = query.trim();
  const exact = trimmed
    && vocab.some(t => t.label.toLowerCase() === trimmed.toLowerCase());
  const showCreate = canCreate && trimmed.length > 0 && !exact;

  // Flat list of selectable options for keyboard nav: existing suggestions, then
  // the optional "create" row.
  const options = useMemo(() => {
    const opts = suggestions.map(t => ({ kind: 'existing', tag: t }));
    if (showCreate) opts.push({ kind: 'create', label: trimmed });
    return opts;
  }, [suggestions, showCreate, trimmed]);

  useEffect(() => { setActive(0); }, [query, open]);

  function close() {
    setOpen(false);
    setQuery('');
    setActive(0);
    setError(null);
  }

  async function applyOption(opt) {
    if (!opt || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (opt.kind === 'existing') await addCaseTag(caseId, { tagId: opt.tag.id });
      else                         await addCaseTag(caseId, { label: opt.label });
      close();
      await onChange?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(tag) {
    if (busy || tag.isSystem) return;
    setBusy(true);
    setError(null);
    try {
      await removeCaseTag(caseId, tag.id);
      await onChange?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown')      { e.preventDefault(); setActive(a => Math.min(a + 1, Math.max(options.length - 1, 0))); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter')     { e.preventDefault(); applyOption(options[active]); }
    else if (e.key === 'Escape')    { e.preventDefault(); close(); }
  }

  return (
    <div className="case-tags">
      {tags.map(tag => (
        <span key={tag.id} className={`badge tag-pill${tag.isSystem ? ' tag-system' : ''}${tagColourClass(tag.colour)}`} style={tagPillStyle(tag.colour)}>
          {tag.isSystem && <LockIcon />}
          {tag.label}
          {canWrite && !tag.isSystem && (
            <button
              type="button"
              className="tag-x"
              title={`Remove ${tag.label}`}
              aria-label={`Remove ${tag.label}`}
              disabled={busy}
              onClick={() => remove(tag)}
            >×</button>
          )}
        </span>
      ))}

      {canWrite && (
        <div className="tag-combobox" ref={boxRef}>
          {!open ? (
            <button type="button" className="tag-add-btn" onClick={() => setOpen(true)}>
              <span aria-hidden="true">+</span> Add tag
            </button>
          ) : (
            <>
              <input
                ref={inputRef}
                className="tag-combobox-input"
                placeholder="Search or create…"
                value={query}
                disabled={busy}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                role="combobox"
                aria-expanded="true"
                aria-autocomplete="list"
              />
              <div className="tag-menu dd-shell" role="listbox">
                {options.length === 0 ? (
                  <div className="tag-menu-empty">
                    {trimmed ? 'No matches' : 'Type to search tags'}
                  </div>
                ) : options.map((opt, i) => (
                  <div
                    key={opt.kind === 'existing' ? `t-${opt.tag.id}` : 'create'}
                    className={`tag-menu-item dd-item${i === active ? ' active' : ''}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={e => { e.preventDefault(); applyOption(opt); }}
                  >
                    {opt.kind === 'existing'
                      ? (<>
                          {opt.tag.isSystem && <LockIcon />}
                          {!opt.tag.isSystem && opt.tag.colour && (
                            <span className={`tag-swatch${tagColourClass(opt.tag.colour)}`} style={tagSwatchStyle(opt.tag.colour)} aria-hidden="true" />
                          )}
                          {opt.tag.label}
                        </>)
                      : (<span className="tag-menu-new">Create “{opt.label}”</span>)}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {error && <span className="muted" style={{ color: 'var(--danger)', fontSize: 11.5 }}>{error}</span>}
    </div>
  );
}
